/**
 * GamesPerformancePanel — per-game financial dashboard for the 4 in-house games.
 *
 * Shows the honest, game-attributable metrics that are meaningful in a
 * single-wallet casino: HANDLE (wagered), WINS PAID, NET HOLD, HOLD %, and
 * ROUNDS. Each card compares actual Hold % against the game's theoretical
 * house edge so admins can spot variance vs. expected math.
 *
 * Time window is admin-selectable (Today / 7 days / 30 days / 90 days).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    RefreshCw, Info, TrendingUp, TrendingDown, Users, Dices, Spade, Circle, Layers, Sparkles,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WINDOWS = [
    { label: 'Today',   days: 1 },
    { label: '7 days',  days: 7 },
    { label: '30 days', days: 30 },
    { label: '90 days', days: 90 },
];

const GAMES = [
    { key: 'blackjack',         label: 'Blackjack',         Icon: Spade,    accent: '#ff3b30' },
    { key: 'craps',             label: 'Craps',             Icon: Dices,    accent: '#39FF14' },
    { key: 'roulette',          label: 'Roulette',          Icon: Circle,   accent: '#c084fc' },
    { key: 'three_card_poker',  label: 'Three-Card Poker',  Icon: Layers,   accent: '#FFD700' },
    { key: 'poker',             label: 'Poker Room (Rake)', Icon: Spade,    accent: '#34D399' },
    { key: 'keno_hearts',       label: '4 Hearts Fire Ball Keno', Icon: Sparkles, accent: '#ff5a5a' },
    { key: 'keno_super_double', label: 'Super Double-Up Keno',    Icon: Sparkles, accent: '#4bd0ff' },
];

const money = (n) => {
    const v = Number(n || 0);
    const s = v < 0 ? '-' : '';
    return `${s}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const pct = (n) => `${Number(n || 0).toFixed(2)}%`;

// Compact SVG sparkline for the daily NET series
const Sparkline = ({ points, accent }) => {
    if (!points || points.length === 0) {
        return <div className="text-[10px] text-white/40 italic h-[46px] flex items-center">No activity in window</div>;
    }
    const w = 260, h = 46, pad = 3;
    const vals = points.map((p) => p.net);
    const min = Math.min(0, ...vals);
    const max = Math.max(0, ...vals);
    const range = max - min || 1;
    const step = (w - pad * 2) / Math.max(1, points.length - 1);
    const yFor = (v) => h - pad - ((v - min) / range) * (h - pad * 2);
    const zeroY = yFor(0);
    const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${pad + i * step} ${yFor(p.net)}`).join(' ');
    return (
        <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="overflow-visible">
            {/* zero baseline */}
            <line x1={pad} x2={w - pad} y1={zeroY} y2={zeroY} stroke="rgba(255,255,255,0.15)" strokeDasharray="2 3" />
            {/* filled area — green above zero, red below */}
            <path d={`${d} L ${pad + (points.length - 1) * step} ${zeroY} L ${pad} ${zeroY} Z`}
                  fill={accent} fillOpacity={0.18} />
            <path d={d} stroke={accent} strokeWidth={1.75} fill="none" />
            {points.map((p, i) => (
                <circle key={i} cx={pad + i * step} cy={yFor(p.net)} r={2}
                        fill={p.net >= 0 ? accent : '#ff3b30'} />
            ))}
        </svg>
    );
};

const GameCard = ({ game, data }) => {
    const { Icon, label, accent, key } = game;
    if (!data) return null;
    const variance = data.variance_vs_theoretical;
    const outperforming = variance >= 0;
    const bigDeviation = Math.abs(variance) >= 3;
    return (
        <div
            className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#111116] to-[#050508] p-4 shadow-[0_0_20px_rgba(0,0,0,0.4)]"
            data-testid={`gp-card-${key}`}
        >
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5" style={{ color: accent }} />
                    <h4 className="font-black text-white">{label}</h4>
                </div>
                <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${
                    data.net >= 0
                        ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'
                        : 'text-red-300 border-red-500/40 bg-red-500/10'
                }`} data-testid={`gp-net-badge-${key}`}>
                    {data.net >= 0 ? 'PROFIT' : 'LOSS'}
                </span>
            </div>

            {/* Big Net */}
            <div className="mb-3">
                <div className="text-[10px] uppercase tracking-wider text-white/50">Net (House)</div>
                <div className={`text-3xl font-black font-mono tabular-nums ${data.net >= 0 ? 'text-white' : 'text-red-400'}`}
                     data-testid={`gp-net-${key}`}>
                    {money(data.net)}
                </div>
            </div>

            {/* Hold vs Theoretical */}
            <div className="rounded-lg bg-black/40 border border-white/10 p-2.5 mb-3">
                <div className="flex items-center justify-between text-[11px] text-white/70">
                    <span>Hold %</span>
                    <span className="text-white/40">Theoretical</span>
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-lg font-black font-mono" data-testid={`gp-hold-${key}`}>{pct(data.hold_pct)}</span>
                    <span className="text-sm font-mono text-white/50">{pct(data.theoretical_hold_pct)}</span>
                </div>
                <div className={`text-[10px] mt-1 flex items-center gap-1 font-semibold ${
                    bigDeviation
                        ? outperforming ? 'text-emerald-300' : 'text-red-300'
                        : 'text-white/50'
                }`}>
                    {outperforming ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {outperforming ? '+' : ''}{Number(variance).toFixed(2)}% vs theoretical
                    {bigDeviation && data.rounds < 500 && <span className="text-white/40">(small sample)</span>}
                </div>
            </div>

            {/* Handle / Paid / Rounds / Players / Avg Bet grid */}
            <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div className="bg-black/30 border border-white/10 rounded-md p-2">
                    <div className="text-white/40 uppercase tracking-wider text-[9px]">Handle</div>
                    <div className="font-mono font-bold text-white" data-testid={`gp-handle-${key}`}>{money(data.handle)}</div>
                </div>
                <div className="bg-black/30 border border-white/10 rounded-md p-2">
                    <div className="text-white/40 uppercase tracking-wider text-[9px]">Wins Paid</div>
                    <div className="font-mono font-bold text-white" data-testid={`gp-paid-${key}`}>{money(data.wins_paid)}</div>
                </div>
                <div className="bg-black/30 border border-white/10 rounded-md p-2">
                    <div className="text-white/40 uppercase tracking-wider text-[9px]">Rounds</div>
                    <div className="font-mono font-bold text-white" data-testid={`gp-rounds-${key}`}>
                        {Number(data.rounds || 0).toLocaleString()}
                    </div>
                </div>
                <div className="bg-black/30 border border-white/10 rounded-md p-2 flex items-center gap-1">
                    <Users className="w-3 h-3 text-white/40" />
                    <div>
                        <div className="text-white/40 uppercase tracking-wider text-[9px]">Players</div>
                        <div className="font-mono font-bold text-white" data-testid={`gp-players-${key}`}>{data.unique_players}</div>
                    </div>
                </div>
                <div className="bg-black/30 border border-white/10 rounded-md p-2 col-span-2">
                    <div className="text-white/40 uppercase tracking-wider text-[9px]">Avg Bet / Round</div>
                    <div className="font-mono font-bold text-white" data-testid={`gp-avg-${key}`}>{money(data.avg_bet)}</div>
                </div>
                {key === 'poker' && (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-md p-2 col-span-2">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-emerald-300/70 uppercase tracking-wider text-[9px]">Dealer Tips (House)</div>
                                <div className="font-mono font-bold text-emerald-200" data-testid="gp-poker-tips">
                                    {money(data.dealer_tips)}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-emerald-300/70 uppercase tracking-wider text-[9px]">House Take</div>
                                <div className="font-mono font-bold text-emerald-200" data-testid="gp-poker-house-take">
                                    {money(data.house_take_total)}
                                </div>
                            </div>
                        </div>
                        <div className="text-[9px] text-emerald-300/60 mt-1">
                            Rake + tips flow to the house account
                        </div>
                    </div>
                )}
            </div>

            {/* Sparkline */}
            <div className="mt-3">
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Daily Net</div>
                <Sparkline points={data.daily} accent={accent} />
            </div>
        </div>
    );
};

export const GamesPerformancePanel = () => {
    const [days, setDays] = useState(7);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/admin/games-performance?days=${days}`, { withCredentials: true });
            setData(res.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load games performance');
        } finally { setLoading(false); }
    }, [days]);

    useEffect(() => { load(); }, [load]);

    const combined = data?.combined || null;
    const games = data?.games || {};

    const rangeLabel = useMemo(() => WINDOWS.find((w) => w.days === days)?.label || `${days} days`, [days]);

    return (
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#0f0f14] to-[#050508] p-4 md:p-5" data-testid="games-performance-panel">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Dices className="w-5 h-5 text-[#FFD700]" />
                        Games Performance
                        <span className="text-xs text-white/40 font-normal">— {rangeLabel} · Central Time</span>
                    </h3>
                    <p className="text-xs text-white/50 mt-0.5">
                        Only what&apos;s game-attributable: handle, wins paid, net, hold %. Loads &amp; cashouts belong to the wallet, not any single game.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="inline-flex bg-black/40 border border-white/10 rounded-lg p-0.5" data-testid="gp-window-switch">
                        {WINDOWS.map((w) => (
                            <button
                                key={w.days}
                                onClick={() => setDays(w.days)}
                                className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${
                                    days === w.days ? 'bg-[#FFD700] text-black' : 'text-white/70 hover:text-white'
                                }`}
                                data-testid={`gp-window-${w.days}`}
                            >
                                {w.label}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={load} disabled={loading}
                        className="p-2 bg-black/40 border border-white/10 rounded-lg text-white/70 hover:text-white disabled:opacity-40"
                        data-testid="gp-refresh"
                        title="Refresh"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Combined KPI banner */}
            {combined && (
                <div className="rounded-xl border border-[#FFD700]/40 bg-gradient-to-r from-[#1a1000] via-[#0a0a0a] to-[#1a1000] p-4 mb-5 grid grid-cols-2 md:grid-cols-6 gap-4"
                     data-testid="gp-combined">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-white/50">Total Handle</div>
                        <div className="text-xl font-black font-mono text-white">{money(combined.handle)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-white/50">Total Wins Paid</div>
                        <div className="text-xl font-black font-mono text-white">{money(combined.wins_paid)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-white/50">Combined Net</div>
                        <div className={`text-2xl font-black font-mono ${combined.net >= 0 ? 'text-[#39FF14]' : 'text-red-400'}`}
                             data-testid="gp-combined-net">
                            {money(combined.net)}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-white/50">Combined Hold %</div>
                        <div className="text-xl font-black font-mono text-white">{pct(combined.hold_pct)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-emerald-300/70">Dealer Tips (House)</div>
                        <div className="text-xl font-black font-mono text-emerald-300" data-testid="gp-combined-tips">
                            {money(combined.dealer_tips || 0)}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-[#FFD700]/80">House Take (Net + Tips)</div>
                        <div className="text-xl font-black font-mono text-[#FFD700]" data-testid="gp-combined-house-take">
                            {money(combined.house_take_total || combined.net)}
                        </div>
                    </div>
                </div>
            )}

            {/* Per-game cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {GAMES.map((g) => (
                    <GameCard key={g.key} game={g} data={games[g.key]} />
                ))}
            </div>

            {/* Reading guide */}
            <div className="mt-5 rounded-lg border border-white/10 bg-black/30 p-3 text-xs text-white/60">
                <div className="flex items-center gap-2 text-white/80 font-bold mb-1">
                    <Info className="w-3.5 h-3.5" />
                    How to read this
                </div>
                <ul className="list-disc pl-5 space-y-1 leading-snug">
                    <li><b>Net</b> = Handle − Wins Paid. That&apos;s the honest house take for the game.</li>
                    <li><b>Hold %</b> = Net ÷ Handle. Compare to theoretical — big variance with a small sample (&lt;500 rounds) is just luck.</li>
                    <li><b>Handle</b> is popularity + volume. A well-loved low-hold game beats an unpopular high-hold one every time.</li>
                    <li><b>Poker Room</b>: Net = total rake collected; Dealer Tips flow entirely to the house wallet. House Take = Rake + Tips.</li>
                    <li>Loads, cashouts, free play, and bonuses live at the <b>wallet level</b> — they&apos;re not tied to any single game.</li>
                </ul>
            </div>
        </div>
    );
};

export default GamesPerformancePanel;
